import { GCM_NONCE_LEN, concatBytes, uint32BigEndian } from './bytes';

// The inverse of async irm/keyservice/wrap.go: any change there ships under a new alg.
export const WRAP_ALG = 'ECDH-P256+HKDF-SHA256+A256GCM/v1';
export const ECDH_P256 = { name: 'ECDH', namedCurve: 'P-256' } as const;
const VIEWER_PARTY_ID = 'orsnn-irm-kr-v1/viewer';
const KEY_SERVICE_PARTY_ID = 'orsnn-irm-kr-v1/keyservice';

export interface UnwrapInput {
  viewerPrivateKey: CryptoKey;
  viewerPublicRaw: Uint8Array;
  serverPublicKey: CryptoKey;
  serverPublicRaw: Uint8Array;
  wrappedKey: Uint8Array;
  downloadId: string;
  jti: string;
}

export function importServerPublicKey(serverPublicRaw: Uint8Array): Promise<CryptoKey> {
  return crypto.subtle.importKey('raw', serverPublicRaw, ECDH_P256, true, []);
}

export async function unwrapDataKey(input: UnwrapInput): Promise<CryptoKey> {
  const { subtle } = crypto;
  const sharedSecret = new Uint8Array(
    await subtle.deriveBits(
      { name: 'ECDH', public: input.serverPublicKey },
      input.viewerPrivateKey,
      256,
    ),
  );
  let hkdfKey: CryptoKey;
  try {
    hkdfKey = await subtle.importKey('raw', sharedSecret, 'HKDF', false, ['deriveKey']);
  } finally {
    sharedSecret.fill(0);
  }
  const kek = await subtle.deriveKey(
    {
      name: 'HKDF',
      hash: 'SHA-256',
      salt: new Uint8Array(0),
      info: fixedInfo(input),
    },
    hkdfKey,
    { name: 'AES-GCM', length: 256 },
    false,
    ['unwrapKey'],
  );
  return subtle.unwrapKey(
    'raw',
    input.wrappedKey.subarray(GCM_NONCE_LEN),
    kek,
    { name: 'AES-GCM', iv: input.wrappedKey.subarray(0, GCM_NONCE_LEN) },
    'AES-GCM',
    false,
    ['decrypt'],
  );
}

function fixedInfo(input: UnwrapInput): Uint8Array {
  const utf8 = new TextEncoder();
  const fields = [
    utf8.encode(VIEWER_PARTY_ID),
    input.viewerPublicRaw,
    utf8.encode(KEY_SERVICE_PARTY_ID),
    input.serverPublicRaw,
    utf8.encode(input.downloadId),
    utf8.encode(input.jti),
  ];
  return concatBytes(fields.flatMap((field) => [uint32BigEndian(field.length), field]));
}
